import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';
import { TID } from '../lib/testIds';

const PORTRAIT =
  'https://customer-assets.emergentagent.com/job_50858299-7c97-46ad-b68c-b59dc25460d7/artifacts/8likali3_71F0E5F6-86FB-4337-A04E-4F9A871EA653.jpg';

const STATS = [
  { k: '3', v: 'Years filming' },
  { k: 'Multiple', v: 'Projects shipped' },
  { k: 'Multiple', v: 'Brands partnered' },
];

export default function About() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const portraitY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);

  return (
    <section
      id="about"
      ref={ref}
      data-testid={TID.aboutRoot}
      className="relative bg-transparent border-t border-white/10 overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
        {/* Portrait */}
        <div className="relative lg:col-span-5 h-[70svh] lg:h-auto lg:min-h-[100svh] overflow-hidden border-b lg:border-b-0 lg:border-r border-white/10">
          <motion.img
            style={{ y: portraitY }}
            src={PORTRAIT}
            alt="UTMEDIA founder portrait"
            className="absolute inset-0 h-[115%] w-full object-cover grayscale contrast-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />

          <div className="absolute bottom-8 left-8 right-8 flex items-end justify-between text-white">
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-white/60 mb-2">
                Founder · Director
              </p>
              <p className="font-display text-xl md:text-2xl font-bold">Ueta Tufuga</p>
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/50 text-right">
              Samoan-Australian<br />Storyteller
            </p>
          </div>
        </div>

        {/* Copy */}
        <div className="lg:col-span-7 px-6 md:px-14 py-20 md:py-28 flex flex-col justify-center">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.6 }}
            transition={{ duration: 0.8 }}
            className="text-[10px] md:text-xs uppercase tracking-[0.3em] text-[#D8FF3A] neon-text-glow mb-8 inline-flex items-center gap-3"
          >
            ◆ The studio
            <span className="inline-block h-px w-10 bg-[#D8FF3A]/50" />
          </motion.p>

          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display font-bold text-white tracking-tighter leading-[1.05] md:leading-[0.95] text-balance mb-10"
            style={{ fontSize: 'clamp(30px, 6vw, 96px)' }}
          >
            Every Story<br />
            <span className="text-white/40">Starts</span> Somewhere.
          </motion.h2>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="space-y-6 text-base md:text-lg text-white/70 leading-relaxed max-w-2xl"
          >
            <p>
              Hey, I&apos;m Ueta, the person behind UTMEDIA. I&apos;m a Newcastle-based videographer who
              loves telling real stories through video. My goal is simple—to create content that
              feels genuine, connects with people, and helps businesses, brands, schools and
              individuals share what makes them unique.
            </p>
            <p>
              Before videography, I spent years playing rugby, which gave me the opportunity to
              travel, meet incredible people and experience different cultures. Those experiences
              shaped the way I tell stories today, always focusing on authenticity and human
              connection.
            </p>
            <p>
              Whether it&apos;s a brand film, event, documentary or social media content, I enjoy working
              closely with my clients to bring their ideas to life and create something we&apos;re both
              proud of.
            </p>
            <p>
              Thanks for stopping by—I look forward to hearing your story.
            </p>
          </motion.div>

          {/* Stats */}
          <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#D8FF3A]/15 border border-[#D8FF3A]/20 max-w-2xl">
            {STATS.map((s, i) => (
              <motion.div
                key={s.v}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.4 }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                className="bg-[#050505] p-6 hover:bg-[#0a0a0a] transition-colors"
              >
                <p className="font-display text-3xl md:text-4xl font-bold text-[#D8FF3A] mb-2 neon-text-glow">{s.k}</p>
                <p className="text-[10px] uppercase tracking-[0.25em] text-white/50">{s.v}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
